import volumeOffImg from '@/assets/images/volumeMute.svg';
import { isReelLike } from '@/helpers/isReelLike';
import { trackStoreeelEvent } from "@/helpers/trackStoreeelEvent";
import { buildMediaAssetViewParams } from "@/helpers/buildMediaAssetViewParams";
import { buildMediaAssetIdParam } from "@/helpers/buildMediaAssetIdParam";
import { isTrackableMediaId } from "@/helpers/isTrackableMediaId";
import { updateGroupSeenBorder } from "@/helpers/updateGroupSeenBorder";
import localStorageRepositories from "@/plugins/localStorageRepositories";
import { setAdBadge } from '@/helpers/adBadge';
import { applyVideoDownloadSafeguards } from '@/helpers/preventVideoDownload';
import { showMediaLoadingShimmer, hideMediaLoadingShimmer } from '@/helpers/mediaLoadingShimmer';
import { queryHeaderElements } from '@/helpers/headerShadow';
import { isReadyThumbnail, pickUrl } from '@/helpers/closedCampaignThumbnail';
import { recordDisplayedContent } from '@/helpers/streamsTargeting';
import { syncFeedFolderAspectFrame } from '@/helpers/feedFolderAspectFrame';
import {
  DEFERRED_SRC_SELECTOR,
  clearDeferredMediaSrc,
  deferMediaSrc,
  materializeDeferredMediaSrc,
  releaseMediaSrc,
  releaseMediaSrcIn,
  waitForMediaSettled,
} from '@/helpers/deferredMediaSrc';

// グローバルタイマー変数
let imageTimeoutId = null;
// タイマー開始時刻とdurationを記録する変数
let imageTimerStartTime = null;
let imageTimerDuration = null;
let imageTimerCallback = null;

const isQuestionnairePlaybackLocked = () => (
  window.__storeeelQuestionnairePlaybackLocked === true
  || Boolean(document.querySelector('[data-storeeel-questionnaire-modal="true"]'))
);

const playVideoIfAllowed = (videoEl) => {
  if (!videoEl) return;
  if (isQuestionnairePlaybackLocked()) {
    try { videoEl.pause?.(); } catch { void 0; }
    return;
  }
  try {
    const p = videoEl.play?.();
    if (p?.catch) p.catch(() => { void 0; });
  } catch { void 0; }
};

const applyMobileMediaLayout = (mediaEl) => {
  if (!mediaEl || !window.storeeelItems?.isMobile) return;
  // iOS Safari の src 切替時に一瞬小さく描画される問題を抑えるため、サイズ/配置を再適用する。
  mediaEl.style.height = '100dvh';
  mediaEl.style.width = '100%';
  mediaEl.style.objectFit = 'contain';
  mediaEl.style.verticalAlign = 'bottom';
  mediaEl.style.position = 'absolute';
  mediaEl.style.top = '50%';
  mediaEl.style.left = '50%';
  mediaEl.style.transform = 'translate3d(-50%, -50%, 0)';
  mediaEl.style.webkitTransform = 'translate3d(-50%, -50%, 0)';
  mediaEl.style.backfaceVisibility = 'hidden';
  mediaEl.style.webkitBackfaceVisibility = 'hidden';
  mediaEl.style.maxHeight = '100dvh';
};

// CTA ホストを wrapper の最後の子（＝最前面）に保つ。
// iOS Safari では translate3d を持つメディア要素（applyMobileMediaLayout 参照）が DOM 順で CTA より
// 後ろにあると、z-index を無視して前面に描画されることがある。コンテンツ切替で後から appendChild した
// 画像/動画が CTA を覆い「同一グループ内で画像→画像に切り替えると CTA ボタンが消える」事象になるため、
// メディア差し替え後に CTA を末尾へ戻して常に前面に保つ。createSlide も [media, ctaHost] の順で生成している。
const ensureCtaHostOnTop = (wrapperEl) => {
  if (!wrapperEl?.querySelector) return;
  const ctaHostEl = wrapperEl.querySelector(':scope > [data-storeeel-cta-host]');
  if (ctaHostEl && wrapperEl.lastElementChild !== ctaHostEl) {
    wrapperEl.appendChild(ctaHostEl);
  }
};

const isMobileStory = () => (
  window.storeeelItems?.isMobile
  && window.storeeelItems?.frame === 'circle'
);

// 動画の読み込み待ちに出すのは、その動画自身の生成済みサムネだけにする。
// グループのサムネ（アイコン用）は別コンテンツの絵なので、ここで出すと切替のたびに一瞬割り込んで見える。
// サムネが無いときは黒で待つ。
const resolveVideoThumbnailUrl = (mediaAsset) => {
  if (!mediaAsset) return '';
  const generatedThumbnailUrl = isReadyThumbnail(mediaAsset)
    ? pickUrl(mediaAsset.thumbnailImageUrl, mediaAsset.thumbnail_image_url)
    : '';
  return pickUrl(
    generatedThumbnailUrl,
    mediaAsset.thumbnailUrl,
    mediaAsset.thumbnail_url,
    mediaAsset.posterUrl,
    mediaAsset.poster_url
  ) || '';
};

const ensureVideoThumbnailElement = (wrapperEl) => {
  if (!wrapperEl) return null;
  let thumbEl = wrapperEl.querySelector('.storeeel-video-thumbnail');
  if (!thumbEl) {
    thumbEl = document.createElement('img');
    thumbEl.className = 'storeeel-video-thumbnail';
    thumbEl.alt = '';
    thumbEl.setAttribute('aria-hidden', 'true');
    thumbEl.style.position = 'absolute';
    thumbEl.style.top = '50%';
    thumbEl.style.left = '50%';
    thumbEl.style.width = '100%';
    thumbEl.style.height = '100dvh';
    thumbEl.style.objectFit = 'contain';
    thumbEl.style.transform = 'translate3d(-50%, -50%, 0)';
    thumbEl.style.webkitTransform = 'translate3d(-50%, -50%, 0)';
    thumbEl.style.backfaceVisibility = 'hidden';
    thumbEl.style.webkitBackfaceVisibility = 'hidden';
    thumbEl.style.pointerEvents = 'none';
    thumbEl.style.zIndex = '1';
    wrapperEl.appendChild(thumbEl);
  }
  return thumbEl;
};

const showVideoThumbnail = (wrapperEl, thumbUrl) => {
  const thumbEl = ensureVideoThumbnailElement(wrapperEl);
  if (!thumbEl) return;
  if (thumbUrl) {
    thumbEl.src = thumbUrl;
    thumbEl.style.background = 'transparent';
  } else if (thumbEl.getAttribute('src')) {
    thumbEl.removeAttribute('src');
    thumbEl.style.background = '#000';
  } else {
    thumbEl.style.background = '#000';
  }
  thumbEl.style.display = 'block';
  thumbEl.style.visibility = 'visible';
  thumbEl.style.opacity = '1';
};

const hideVideoThumbnail = (wrapperEl) => {
  const thumbEl = wrapperEl?.querySelector?.('.storeeel-video-thumbnail');
  if (!thumbEl) return;
  thumbEl.style.display = 'none';
  thumbEl.style.visibility = 'hidden';
  thumbEl.style.opacity = '0';
};

const cleanupVideoThumbnailGuard = (videoEl) => {
  if (!videoEl) return;
  if (videoEl._storeeelThumbFallbackTimerId != null) {
    clearTimeout(videoEl._storeeelThumbFallbackTimerId);
    videoEl._storeeelThumbFallbackTimerId = null;
  }
  if (videoEl._storeeelThumbOnReady) {
    videoEl.removeEventListener('loadedmetadata', videoEl._storeeelThumbOnReady);
    videoEl.removeEventListener('loadeddata', videoEl._storeeelThumbOnReady);
    videoEl.removeEventListener('canplay', videoEl._storeeelThumbOnReady);
    videoEl._storeeelThumbOnReady = null;
  }
};

const cleanupPendingImageReveal = (imageEl) => {
  if (!imageEl) return;
  if (imageEl._storeeelOnPendingImageLoad) {
    imageEl.removeEventListener('load', imageEl._storeeelOnPendingImageLoad);
    imageEl._storeeelOnPendingImageLoad = null;
  }
  if (imageEl._storeeelOnPendingImageError) {
    imageEl.removeEventListener('error', imageEl._storeeelOnPendingImageError);
    imageEl._storeeelOnPendingImageError = null;
  }
};

export const isMediaImageReady = (imageEl) => (
  Boolean(
    imageEl
    && imageEl.dataset.storeeelPendingLoad !== '1'
    && imageEl.complete
    && imageEl.naturalWidth > 0
    && imageEl.naturalHeight > 0
  )
);

// 画像が表示済み（隠されておらず、読み込み済みの画像を描画している）か
const isImageShowingPicture = (imageEl) => (
  imageEl.style.display !== 'none'
  && imageEl.style.visibility !== 'hidden'
  && imageEl.style.opacity !== '0'
  && Boolean(imageEl.getAttribute('src'))
  && imageEl.complete
  && imageEl.naturalWidth > 0
  && imageEl.naturalHeight > 0
);

// 旧画像を出したまま裏で読み込んでいる新画像の差し替えを取りやめる（読み込み中なら取得も打ち切る）。
// src を直接書き換える処理や、画像を隠して動画を出す処理の前に呼び、後から古い画像で上書きされないようにする。
// 取りやめた場合は true を返す。
export const cancelPendingImageSwap = (imageEl) => {
  if (!imageEl) return false;
  imageEl._storeeelImageSwapSeq = (imageEl._storeeelImageSwapSeq || 0) + 1;
  const loader = imageEl._storeeelImageSwapLoader;
  if (!loader) return false;
  imageEl._storeeelImageSwapLoader = null;
  loader.removeAttribute('src');
  return true;
};

export const setMediaImageContent = (imageEl, content, mediaAsset) => {
  if (!imageEl || !content || !mediaAsset) return;

  cleanupPendingImageReveal(imageEl);
  cancelPendingImageSwap(imageEl);
  const swapSeq = imageEl._storeeelImageSwapSeq;
  const url = mediaAsset.mediaUrl;
  const isSameSrc = imageEl.getAttribute('src') === url;
  // 画像→画像の切り替えで旧画像を一旦隠すと、新画像の decode が終わるまで背景（黒）が見えてチカチカする。
  // 旧画像が表示済みなら、新画像を裏で読み込み・decode し終えてから src を差し替え、隠さずに入れ替える。
  // 同じ画像（グループ入場時に先頭へ戻し済み等）なら src を入れ直さず、隠しもしない。
  const keepCurrentPicture = isImageShowingPicture(imageEl);
  imageEl.dataset.storeeelPendingLoad = '1';
  if (!keepCurrentPicture) {
    // 旧画像が見え続けないよう、新画像の描画完了まで一旦隠す。
    imageEl.style.opacity = '0';
  }
  if (!keepCurrentPicture && !isSameSrc) {
    imageEl.src = url;
  }
  imageEl.setAttribute('media-name', mediaAsset.name);
  imageEl.setAttribute('media-id', mediaAsset.id);
  imageEl.setAttribute('content-id', content.id);
  if (content.contentGroupId != null) {
    imageEl.setAttribute('group-id', content.contentGroupId);
  }

  const reveal = () => {
    imageEl.dataset.storeeelPendingLoad = '0';
    imageEl.style.opacity = '1';
    cleanupPendingImageReveal(imageEl);
    // SP では使い回しの <img> にキャッシュ済み画像の src を差し替えると 'load' が発火しない
    // ことがあり、'load' 依存のシマー解除・進捗開始・自動送りタイマー設定が走らず固まる
    // （特に同一グループの画像→画像の自動送りで発生）。画像が実際に表示されたこの瞬間に、
    // シマーを確実に消し、登録済みの進捗/自動送りハンドラを明示的に駆動してフォールバックする。
    // これらのハンドラは準備状態を再判定し成功時に自身を解除する冪等な実装のため、
    // 'load' が別途発火しても二重実行されない。
    const wrapperEl = imageEl.closest?.('.slide-media-wrapper');
    if (wrapperEl) hideMediaLoadingShimmer(wrapperEl);
    try { imageEl._onLoadForProgress?.(); } catch { void 0; }
    try { imageEl._onLoadForAutoPlay?.(); } catch { void 0; }
  };

  imageEl._storeeelOnPendingImageLoad = reveal;
  imageEl._storeeelOnPendingImageError = () => {
    imageEl.dataset.storeeelPendingLoad = '0';
    imageEl.style.opacity = '1';
    cleanupPendingImageReveal(imageEl);
  };
  imageEl.addEventListener('load', imageEl._storeeelOnPendingImageLoad);
  imageEl.addEventListener('error', imageEl._storeeelOnPendingImageError);

  if (keepCurrentPicture && !isSameSrc) {
    // 同じ URL を読むため、差し替え時の <img> はメモリキャッシュから即座に表示でき、通信は増えない。
    // 読み込み中に次の切り替えが来たら cancelPendingImageSwap で取得ごと打ち切る（src を差し替えたときと同じ）。
    const loader = new Image();
    imageEl._storeeelImageSwapLoader = loader;
    const swap = () => {
      if (imageEl._storeeelImageSwapSeq !== swapSeq) return;
      imageEl._storeeelImageSwapLoader = null;
      // decode 済みの画像を描画時にも同期で decode させ、差し替えの瞬間に空白を挟ませない
      imageEl.decoding = 'sync';
      imageEl.src = url;
      if (imageEl.complete && imageEl.naturalWidth > 0 && imageEl.naturalHeight > 0) {
        reveal();
      }
      // 即座に揃わない場合は load / error で従来どおり表示する
    };
    loader.src = url;
    if (typeof loader.decode === 'function') {
      loader.decode().then(swap).catch(swap);
    } else {
      loader.onload = swap;
      loader.onerror = swap;
    }
    return;
  }

  if (typeof imageEl.decode === 'function') {
    imageEl.decode()
      .then(() => { reveal(); })
      .catch(() => {
        if (imageEl.complete && imageEl.naturalWidth > 0 && imageEl.naturalHeight > 0) {
          reveal();
        }
      });
    return;
  }

  if (imageEl.complete && imageEl.naturalWidth > 0 && imageEl.naturalHeight > 0) {
    requestAnimationFrame(() => { reveal(); });
  }
};

// 読み込み中はサムネ表示で縮みを見せない（基本はSP+circle、必要に応じて強制表示）
export const applyVideoThumbnailGuard = (videoEl, wrapperEl, content, mediaAsset, options = {}) => {
  const { force = false } = options;
  if (!force && !isMobileStory()) {
    hideVideoThumbnail(wrapperEl);
    return;
  }

  cleanupVideoThumbnailGuard(videoEl);
  const thumbUrl = resolveVideoThumbnailUrl(mediaAsset);
  showVideoThumbnail(wrapperEl, thumbUrl);
  if (videoEl) {
    videoEl.style.opacity = '0';
  }

  const reveal = () => {
    cleanupVideoThumbnailGuard(videoEl);
    if (videoEl) {
      applyMobileMediaLayout(videoEl);
      videoEl.style.opacity = '1';
      const isPc = !window.storeeelItems?.isMobile;
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      const isActiveVideo = !isPc || (activeSlide && activeSlide.contains(videoEl));
      if (isActiveVideo) {
        playVideoIfAllowed(videoEl);
      } else {
        // PCでは中央以外の動画を再生しない
        videoEl.autoplay = false;
        videoEl.removeAttribute('autoplay');
        try { videoEl.pause(); } catch { void 0; }
        try { videoEl.currentTime = 0; } catch { void 0; }
      }
    }
    hideVideoThumbnail(wrapperEl);
  };

  videoEl._storeeelThumbOnReady = reveal;
  videoEl.addEventListener('loadedmetadata', reveal);
  videoEl.addEventListener('loadeddata', reveal);
  videoEl.addEventListener('canplay', reveal);
  videoEl._storeeelThumbFallbackTimerId = window.setTimeout(() => {
    if (videoEl.readyState >= 1) reveal();
  }, 1200);
  if (videoEl.readyState >= 1) {
    requestAnimationFrame(reveal);
  }
};

const setStoryVideoRole = (videoEl, role) => {
  if (!videoEl) return;
  if (role === 'active') {
    videoEl.classList.add('storeeel-movie');
    videoEl.classList.remove('storeeel-movie-buffer');
    videoEl.autoplay = true;
    videoEl.setAttribute('autoplay', '');
    videoEl.dataset.storeeelRole = 'active';
    return;
  }
  videoEl.classList.remove('storeeel-movie');
  videoEl.classList.add('storeeel-movie-buffer');
  videoEl.autoplay = false;
  videoEl.removeAttribute('autoplay');
  videoEl.dataset.storeeelRole = 'buffer';
};

const setupStoryVideoElement = (videoEl) => {
  if (!videoEl) return;
  applyVideoDownloadSafeguards(videoEl);
  videoEl.setAttribute('playsinline', '');
  videoEl.setAttribute('webkit-playsinline', '');
  videoEl.setAttribute('muted', '');
  videoEl.muted = true;
  videoEl.preload = 'auto';
  applyMobileMediaLayout(videoEl);
};

// ストーリー型（SP）の動画読み込み順制御。
// 「表示中の動画 → グループ内の次の動画 → 前後グループの先頭動画」の順に、前の読み込みが落ち着いてから
// 1本ずつ src を入れる（src の保留は deferredMediaSrc.js）。
let storyPreloadSeq = 0;

const findVisibleStoryVideo = (wrapperEl) => (
  Array.from(wrapperEl?.querySelectorAll?.('video.storeeel-movie') || [])
    .find((videoEl) => videoEl.style.display !== 'none') || null
);

// 前後グループのスライドを「次 → 前」の順で返す。
// loop 時の Swiper は遷移を始めるときにスライドの DOM 順を並べ替えるため、DOM 上の隣が前後のグループとは限らない
// （最後↔先頭をまたいだ後は表示中が DOM の端に来て、片側の隣が DOM 上にいなくなる）。
// DOM の隣で判定すると入ってくる面が読み込まれず・解放されて真っ暗のまま回転するため、
// loop 時は data-swiper-slide-index（元のグループ順）で前後を求め、最後↔先頭も隣として扱う。
const findStoryNeighborSlides = (wrapperEl) => {
  const slide = wrapperEl?.closest?.('.swiper-slide');
  const idxAttr = slide?.getAttribute?.('data-swiper-slide-index');
  const container = slide?.parentElement;
  if (idxAttr == null || !container) {
    return { slide, neighbors: [slide?.nextElementSibling, slide?.previousElementSibling].filter(Boolean) };
  }
  const slides = Array.from(container.querySelectorAll(':scope > .swiper-slide[data-swiper-slide-index]'));
  // 枚数ではなく index の最大値から数える（同じ index のスライドが混ざっても、存在しない index を探さないため）
  const count = Math.max(...slides.map((s) => Number(s.getAttribute('data-swiper-slide-index')))) + 1;
  const idx = Number(idxAttr);
  const findByIndex = (target) => slides.find((s) => Number(s.getAttribute('data-swiper-slide-index')) === target);
  const neighbors = [(idx + 1) % count, (idx - 1 + count) % count]
    .map(findByIndex)
    .filter((s, i, arr) => s && s !== slide && arr.indexOf(s) === i);
  return { slide, neighbors };
};

// 読み込み範囲（表示中グループの表示中＋グループ内の次 / 前後グループの先頭）以外の動画を解放する。
// 通り過ぎた動画を pause だけで残すと通信が開いたまま溜まり、数本で後続の動画が届かなくなる。
const releaseStoryMediaOutsideWindow = (wrapperEl) => {
  const { slide, neighbors } = findStoryNeighborSlides(wrapperEl);
  const container = slide?.parentElement;
  if (!container) return;
  container.querySelectorAll(':scope > .swiper-slide').forEach((otherSlide) => {
    if (otherSlide === slide) return;
    otherSlide.querySelectorAll('video').forEach((videoEl) => {
      // 前後グループは先頭コンテンツ（表示役の video）だけを残し、先読み役は解放する
      if (neighbors.includes(otherSlide) && !videoEl.classList.contains('storeeel-movie-buffer')) return;
      // サムネイル待ち（applyVideoThumbnailGuard）の後始末はしない。読み直したときに従来どおり表示・再生させるため
      releaseMediaSrc(videoEl);
    });
  });
};

// 先頭へ戻したグループが読み込み範囲の外なら、src を残さず解放する（表示中のスライドを基準に判定）
const releaseStoryMediaAroundActiveSlide = () => {
  const activeWrapper = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper');
  if (activeWrapper) releaseStoryMediaOutsideWindow(activeWrapper);
};

// 前後グループの先頭コンテンツを 1 件ずつ読み込む（次のグループ → 前のグループの順）
const preloadNeighborGroupMedia = async (wrapperEl, seq) => {
  const { neighbors } = findStoryNeighborSlides(wrapperEl);
  for (const neighborSlide of neighbors) {
    if (seq !== storyPreloadSeq) return;
    const mediaEl = neighborSlide?.querySelector?.(DEFERRED_SRC_SELECTOR);
    if (!mediaEl || !materializeDeferredMediaSrc(mediaEl)) continue;
    if (mediaEl.tagName === 'VIDEO') {
      try { mediaEl.load(); } catch { void 0; }
    }
    await waitForMediaSettled(mediaEl);
  }
};

const setStoryVideoContent = (videoEl, content, mediaAsset, { defer = false } = {}) => {
  if (!videoEl || !content || !mediaAsset) return;
  setupStoryVideoElement(videoEl);
  if (defer) {
    deferMediaSrc(videoEl, mediaAsset.mediaUrl);
  } else {
    clearDeferredMediaSrc(videoEl);
    videoEl.src = mediaAsset.mediaUrl;
  }
  videoEl.dataset.type = mediaAsset.name;
  videoEl.dataset.storeeelContentId = String(content.id);
  videoEl.setAttribute('media-name', mediaAsset.name);
  videoEl.setAttribute('media-id', mediaAsset.id);
  videoEl.setAttribute('content-id', content.id);
  if (content.contentGroupId != null) {
    videoEl.setAttribute('group-id', content.contentGroupId);
  }
};

const hideVideoElement = (videoEl) => {
  if (!videoEl) return;
  try { videoEl.pause(); } catch { void 0; }
  videoEl.style.display = 'none';
  videoEl.style.visibility = 'hidden';
  videoEl.style.opacity = '0';
};

const showVideoElement = (videoEl) => {
  if (!videoEl) return;
  videoEl.style.display = 'block';
  videoEl.style.visibility = 'visible';
};

const notifyActiveVideoChange = (videoEl) => {
  if (!videoEl) return;
  try {
    window.dispatchEvent(new CustomEvent('storeeel-active-video-changed', { detail: { video: videoEl } }));
  } catch { void 0; }
};

const ensureStoryVideoPair = (wrapperEl) => {
  if (!wrapperEl) return { activeVideo: null, bufferVideo: null };
  const videos = Array.from(wrapperEl.querySelectorAll('video'));
  let activeVideo = videos.find((videoEl) => videoEl.classList.contains('storeeel-movie')) || null;
  let bufferVideo = videos.find((videoEl) => videoEl.classList.contains('storeeel-movie-buffer')) || null;

  if (!activeVideo && videos.length > 0) {
    activeVideo = videos[0];
    setStoryVideoRole(activeVideo, 'active');
  }
  if (!activeVideo) {
    activeVideo = document.createElement('video');
    setStoryVideoRole(activeVideo, 'active');
    setupStoryVideoElement(activeVideo);
    wrapperEl.appendChild(activeVideo);
    // 新規生成直後は display 未設定（''）で「表示中」と誤判定され得る。
    // 次コンテンツのプリロード（preloadNextStoryVideo）でこの active 動画が作られると、
    // 画像コンテンツ表示中でも進捗分岐 `video.style.display !== 'none'` が動画側を選び、
    // 進捗バーが止まる。実際に動画を出す swapStoryVideo は直後に showVideoElement で表示するため、
    // ここでは既定で非表示にしておく。
    hideVideoElement(activeVideo);
  }
  if (activeVideo) {
    setupStoryVideoElement(activeVideo);
  }
  if (activeVideo && !activeVideo.dataset.storeeelContentId) {
    const existingContentId = activeVideo.getAttribute('content-id');
    if (existingContentId) {
      activeVideo.dataset.storeeelContentId = String(existingContentId);
    }
  }
  if (!bufferVideo) {
    bufferVideo = document.createElement('video');
    setStoryVideoRole(bufferVideo, 'buffer');
    setupStoryVideoElement(bufferVideo);
    wrapperEl.appendChild(bufferVideo);
  }
  if (bufferVideo) {
    hideVideoElement(bufferVideo);
  }
  return { activeVideo, bufferVideo };
};

export const swapStoryVideo = (wrapperEl, content, mediaAsset) => {
  const { activeVideo, bufferVideo } = ensureStoryVideoPair(wrapperEl);
  if (!activeVideo || !bufferVideo) return { activeVideo: null, bufferVideo: null };
  cleanupVideoThumbnailGuard(activeVideo);
  cleanupVideoThumbnailGuard(bufferVideo);

  const targetId = String(content.id);
  const activeId = activeVideo.dataset.storeeelContentId;
  const bufferId = bufferVideo.dataset.storeeelContentId;

  let nextActive = activeVideo;
  let nextBuffer = bufferVideo;
  const activeMatches = activeId === targetId;
  const bufferMatches = bufferId === targetId;

  if (bufferMatches && !activeMatches) {
    nextActive = bufferVideo;
    nextBuffer = activeVideo;
  } else if (!activeMatches) {
    nextActive = bufferVideo;
    nextBuffer = activeVideo;
    setStoryVideoContent(nextActive, content, mediaAsset);
  }

  setStoryVideoRole(nextActive, 'active');
  nextActive.classList.add('active');
  showVideoElement(nextActive);

  if (nextBuffer) {
    setStoryVideoRole(nextBuffer, 'buffer');
    nextBuffer.classList.remove('active');
    hideVideoElement(nextBuffer);
  }

  if (wrapperEl.firstChild !== nextActive) {
    wrapperEl.insertBefore(nextActive, wrapperEl.firstChild);
  }

  // 読み込み順待ちで src が保留されていた（解放された先読み役を含む）場合は、ここで最優先に読み込む
  const materialized = (activeMatches || bufferMatches) && materializeDeferredMediaSrc(nextActive);

  if (!activeMatches) {
    applyVideoThumbnailGuard(nextActive, wrapperEl, content, mediaAsset);
  } else {
    hideVideoThumbnail(wrapperEl);
    nextActive.style.opacity = '1';
  }

  if (!activeMatches || materialized) {
    try { nextActive.load(); } catch { void 0; }
  }
  playVideoIfAllowed(nextActive);

  notifyActiveVideoChange(nextActive);

  return { activeVideo: nextActive, bufferVideo: nextBuffer };
};

// グループ内の次の動画を buffer 側に読み込ませる。読み込みを始めた buffer を返す
const loadNextStoryVideoInGroup = (wrapperEl, nextContent) => {
  const nextAsset = nextContent?.mediaAsset;
  const nextMime = nextAsset?.attachmentData?.metadata?.mime_type || '';
  if (!nextContent || !nextMime.includes('video')) return null;

  const { activeVideo, bufferVideo } = ensureStoryVideoPair(wrapperEl);
  if (!bufferVideo || bufferVideo === activeVideo) return null;
  const targetId = String(nextContent.id);
  if (activeVideo?.dataset.storeeelContentId === targetId) return null;
  if (bufferVideo?.dataset.storeeelContentId === targetId) {
    // 範囲外で解放されていた場合は読み直す
    if (materializeDeferredMediaSrc(bufferVideo)) {
      try { bufferVideo.load(); } catch { void 0; }
    }
    return bufferVideo;
  }

  setStoryVideoRole(bufferVideo, 'buffer');
  cleanupVideoThumbnailGuard(bufferVideo);
  setStoryVideoContent(bufferVideo, nextContent, nextAsset);
  hideVideoElement(bufferVideo);
  try { bufferVideo.load(); } catch { void 0; }
  return bufferVideo;
};

// 表示中の動画が落ち着いてから「グループ内の次 → 前後グループの先頭」を順に先読みする。
// コンテンツ/グループが切り替わったら seq が進み、古い先読みの続きは打ち切られる。
export const preloadNextStoryVideo = (wrapperEl, contents) => {
  if (!isMobileStory()) return;
  storyPreloadSeq += 1;
  const seq = storyPreloadSeq;
  releaseStoryMediaOutsideWindow(wrapperEl);
  const nextContent = contents?.[Number(window.moviePosition || 0) + 1];

  (async () => {
    await waitForMediaSettled(findVisibleStoryVideo(wrapperEl));
    if (seq !== storyPreloadSeq) return;
    await waitForMediaSettled(loadNextStoryVideoInGroup(wrapperEl, nextContent));
    if (seq !== storyPreloadSeq) return;
    await preloadNeighborGroupMedia(wrapperEl, seq);
  })().catch(() => { void 0; });
};

// ストーリー型で「非アクティブのグループ」を先頭コンテンツに戻す（再生/計測はしない）
export const resetStorySlideMedia = (wrapperEl, content) => {
  if (!isMobileStory()) return;
  if (!wrapperEl || !content) return;
  const mediaAsset = content.mediaAsset;
  if (!mediaAsset) return;
  const mimeType = mediaAsset?.attachmentData?.metadata?.mime_type || '';

  wrapperEl.dataset.assetType = mediaAsset?.type || '';
  setAdBadge(wrapperEl, mediaAsset?.type === 'adMediaAsset');

  const hideAllVideos = () => {
    wrapperEl.querySelectorAll('video').forEach((videoEl) => {
      cleanupVideoThumbnailGuard(videoEl);
      try { videoEl.pause(); } catch { void 0; }
      try { videoEl.currentTime = 0; } catch { void 0; }
      videoEl.style.display = 'none';
      videoEl.style.visibility = 'hidden';
      videoEl.style.opacity = '0';
      videoEl.classList.remove('active');
    });
  };

  const hideAllImages = () => {
    wrapperEl.querySelectorAll('img.storeeel-media-image').forEach((imgEl) => {
      cancelPendingImageSwap(imgEl);
      imgEl.style.display = 'none';
      imgEl.style.visibility = 'hidden';
      imgEl.style.opacity = '0';
      imgEl.classList.remove('active');
    });
  };

  if (mimeType.includes('image')) {
    hideAllVideos();
    // 先頭が画像なら動画は使わないため解放する
    releaseMediaSrcIn(wrapperEl);
    hideVideoThumbnail(wrapperEl);

    let imageEl = wrapperEl.querySelector('img.storeeel-media-image');
    if (!imageEl) {
      imageEl = document.createElement('img');
      imageEl.className = 'storeeel-media-image';
      wrapperEl.appendChild(imageEl);
    }

    // 旧画像を出したまま待っている差し替えがあれば取りやめ、先頭の画像で上書きされないようにする
    if (cancelPendingImageSwap(imageEl)) imageEl.dataset.storeeelPendingLoad = '0';
    imageEl.src = mediaAsset.mediaUrl;
    imageEl.style.display = 'block';
    imageEl.style.visibility = 'visible';
    imageEl.style.opacity = '1';
    imageEl.setAttribute('media-name', mediaAsset.name);
    imageEl.setAttribute('media-id', mediaAsset.id);
    imageEl.setAttribute('content-id', content.id);
    if (content.contentGroupId != null) {
      imageEl.setAttribute('group-id', content.contentGroupId);
    }
    imageEl.classList.add('active');
    applyMobileMediaLayout(imageEl);
    // 先頭コンテンツへ戻した内容に合わせて、CTA基準に使うフィードフォルダのマークも更新する
    syncFeedFolderAspectFrame(wrapperEl, content, imageEl);
    ensureCtaHostOnTop(wrapperEl);
    releaseStoryMediaAroundActiveSlide();
    return;
  }

  if (!mimeType.includes('video')) return;

  hideAllImages();

  let activeVideo = wrapperEl.querySelector('video.storeeel-movie') || wrapperEl.querySelector('video');
  if (!activeVideo) {
    activeVideo = document.createElement('video');
    wrapperEl.appendChild(activeVideo);
  }

  wrapperEl.querySelectorAll('video').forEach((videoEl) => {
    if (videoEl === activeVideo) return;
    cleanupVideoThumbnailGuard(videoEl);
    hideVideoElement(videoEl);
    videoEl.classList.remove('active');
    // 先頭へ戻したグループの先読み役は不要になるため解放する
    releaseMediaSrc(videoEl);
  });

  activeVideo.classList.add('storeeel-movie');
  activeVideo.classList.remove('storeeel-movie-buffer');
  activeVideo.dataset.storeeelRole = 'active';
  activeVideo.autoplay = false;
  activeVideo.removeAttribute('autoplay');

  cleanupVideoThumbnailGuard(activeVideo);
  // 同じ動画が既に入っていればそのまま先頭へ戻す。別の動画に差し替える場合は、
  // 表示中グループの読み込みを邪魔しないよう src を保留し、先読みの順番が来たら読み込む
  const keepCurrentSrc = activeVideo.getAttribute('src') === mediaAsset.mediaUrl;
  setStoryVideoContent(activeVideo, content, mediaAsset, { defer: !keepCurrentSrc });
  showVideoElement(activeVideo);
  activeVideo.classList.add('active');
  try { activeVideo.pause(); } catch { void 0; }
  try { activeVideo.currentTime = 0; } catch { void 0; }

  // 非アクティブのグループでは、遷移先の隣面に入った瞬間にグループサムネイルが見えてしまうため、
  // 先頭動画へ戻すときもサムネイルは前面に出さず、動画本体を裏側で読み込ませておく。
  hideVideoThumbnail(wrapperEl);
  activeVideo.style.opacity = '1';

  // 動画はフィードフォルダ商品になり得ないため、残っているマーク/枠を解除する
  syncFeedFolderAspectFrame(wrapperEl, content, activeVideo);

  try { activeVideo.load(); } catch { void 0; }
  ensureCtaHostOnTop(wrapperEl);
  releaseStoryMediaAroundActiveSlide();
};

// タイマーをクリアする関数（モーダル閉じ後の自動送りを防ぐためにも使用）
export const clearImageTimer = () => {
  if (imageTimeoutId !== null) {
    clearTimeout(imageTimeoutId);
    imageTimeoutId = null;
    // タイマー情報もリセット
    imageTimerStartTime = null;
    imageTimerDuration = null;
    imageTimerCallback = null;
  }
};

// タイマーをセットする関数
export const setImageTimer = (callback, duration) => {
  clearImageTimer();
  // タイマー開始時刻とdurationを記録
  imageTimerStartTime = Date.now();
  imageTimerDuration = duration;
  imageTimerCallback = callback;
  imageTimeoutId = setTimeout(() => {
    imageTimerCallback?.();
  }, duration);
};

// 共通のメディア切り替え処理
export const commonChangeMediaProcess = (contents) => {
  // メディアが切り替わるときにタイマーをクリア
  clearImageTimer();

  const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
  if (!activeSlide) return;
  const movie = activeSlide.querySelector('.storeeel-movie');
  const image = activeSlide.querySelector('.storeeel-media-image');
  const content = contents[window.moviePosition];
  const mediaAsset = content.mediaAsset;
  const contentId = content.id;
  const mimeType = mediaAsset.attachmentData.metadata.mime_type;
  const volumeIcon = document.querySelector('#volume-desktop');
  const slideMediaWrapper = activeSlide.querySelector('.storeeel-swiper-group-wrapper .slide-media-wrapper');
  if (!slideMediaWrapper) return;
  showMediaLoadingShimmer(slideMediaWrapper);
  if (slideMediaWrapper) {
    slideMediaWrapper.dataset.assetType = mediaAsset?.type || '';
  }
  setAdBadge(slideMediaWrapper, mediaAsset?.type === 'adMediaAsset');

  const shouldTemporarilyHideWrapper = !(window.storeeelItems?.isMobile);
  if (shouldTemporarilyHideWrapper) {
    slideMediaWrapper.style.visibility = 'hidden';
  } else {
    slideMediaWrapper.style.visibility = 'visible';
  }

  // コンテンツが実際に表示された瞬間に頻度挿入の状態を更新する（通常=カウント加算 / freq=閲覧済み記録）。
  // moviePosition に依存せず表示の瞬間に積むため、スワイプ操作の違いによる数え漏れ・二重カウントを防ぐ。
  recordDisplayedContent(content, window._storeeelFreqInsertCircleState);

  // リール型(square)・タイル型(tile)はグループ概念がないため group_id = null。
  // ストーリー型(circle)は DOM の group-id 属性 → window.iconId の順で解決する。
  const groupId = isReelLike(window.storeeelItems?.frame)
    ? null
    : (slideMediaWrapper?.getAttribute?.('group-id') || window.iconId || null);

  // media_id が空のイベント/ログは集計に乗らないため送らない（isTrackableMediaId のコメント参照）
  const trackedMediaId = content?.isFrequencyInsertContent
    ? content.frequencyInsertContentId
    : mediaAsset?.id;

  if (isTrackableMediaId(trackedMediaId)) {
    if (content?.isFrequencyInsertContent) {
      trackStoreeelEvent('storeeel_frequency_insert_media_view', {
        storeeel_name: mediaAsset.name,
        ...buildMediaAssetIdParam(content.frequencyInsertContentId, mediaAsset?.type),
        campaign_id: window.storeeelItems.id,
        ...(groupId ? { group_id: groupId } : {}),
      });
    } else {
      // フィードフォルダ商品も通常イベントで計測する。mediaAsset.id が
      // `{content hashid}:{商品ID}` の複合IDになっており、API側で商品単位に集計される
      trackStoreeelEvent('storeeel_media_asset_view', buildMediaAssetViewParams({
        storeeelName: mediaAsset.name,
        groupId,
        mediaId: mediaAsset.id,
        campaignId: window.storeeelItems.id,
        mediaAssetType: mediaAsset?.type,
      }))
    }
  }
  if (isTrackableMediaId(mediaAsset?.id)) {
    localStorageRepositories.get("mediaView").set(
      mediaAsset.id,
      window.storeeelItems.id,
      groupId,
      mediaAsset?.type ?? null
    )
  }
  // 無期限の既読フラグも保存
  try {
    if (!isReelLike(window.storeeelItems?.frame)) {
      localStorageRepositories.get("contentSeen").set(contentId);
      updateGroupSeenBorder(groupId);
    }
  } catch { void 0; }

  const hideAllVideosInWrapper = () => {
    slideMediaWrapper.querySelectorAll('video').forEach((videoEl) => {
      cleanupVideoThumbnailGuard(videoEl);
      if (typeof videoEl.pause === 'function') {
        try {
          videoEl.pause();
        } catch { void 0; }
      }
      try {
        videoEl.currentTime = 0;
      } catch { void 0; }
      videoEl.muted = true;
      videoEl.style.display = 'none';
      videoEl.style.visibility = 'hidden';
      videoEl.style.opacity = '0';
      videoEl.classList.remove('active');
    });
  };

  const hideAllImagesInWrapper = () => {
    slideMediaWrapper.querySelectorAll('img').forEach((imgEl) => {
      cancelPendingImageSwap(imgEl);
      imgEl.style.display = 'none';
      imgEl.style.visibility = 'hidden';
      imgEl.style.opacity = '0';
      imgEl.classList.remove('active');
    });
  };

  if (mimeType.includes('image')) {
    // 画像表示時は、背面に動画が残らないように全動画を確実に非表示にする
    hideAllVideosInWrapper();
    hideVideoThumbnail(slideMediaWrapper);

    if (image) {
      image.style.display = 'block';
      image.style.visibility = 'visible';
      image.classList.add('active');
      applyMobileMediaLayout(image);
      setMediaImageContent(image, content, mediaAsset);
    } else {
      const imageElement = document.createElement('img');
      imageElement.className = 'storeeel-media-image';
      imageElement.style.objectFit = 'contain';
      imageElement.style.display = 'block';
      imageElement.style.visibility = 'visible';
      imageElement.classList.add('active');
      applyMobileMediaLayout(imageElement);
      setMediaImageContent(imageElement, content, mediaAsset);
      slideMediaWrapper.appendChild(imageElement);
    }
    // フィードフォルダ商品は任意比率の商品画像のため、枠を9:16へ強制する（通常コンテンツでは元に戻す）
    const activeImageEl = slideMediaWrapper.querySelector('.storeeel-media-image');
    syncFeedFolderAspectFrame(slideMediaWrapper, content, activeImageEl);
    // 画像表示時は音量ボタンを隠す
    if (volumeIcon) {
      volumeIcon.style.visibility = 'hidden';
    }
  } else {
    clearImageTimer();
    // 動画表示時は、背面に画像が残らないように全画像を確実に非表示にする
    hideAllImagesInWrapper();

    let activeVideoEl = null;
    // モバイルのストーリー型は 2 本の video を交互に使い、縮みを見せない。
    const useStorySwap = isMobileStory();

    if (useStorySwap) {
      const result = swapStoryVideo(slideMediaWrapper, content, mediaAsset);
      activeVideoEl = result.activeVideo;
    } else if (movie) {
      applyVideoDownloadSafeguards(movie);
      movie.src = mediaAsset.mediaUrl;
      movie.dataset.type = mediaAsset.name;
      movie.style.display = 'block';
      movie.style.visibility = 'visible';
      movie.style.opacity = '1';
      movie.setAttribute('media-name', mediaAsset.name);
      movie.setAttribute('media-id', mediaAsset.id);
      movie.setAttribute('content-id', contentId);
      movie.classList.add('active');
      applyMobileMediaLayout(movie);
      activeVideoEl = movie;
    } else {
      const videoElement = document.createElement('video');
      videoElement.src = mediaAsset.mediaUrl;
      videoElement.className = 'storeeel-movie';
      videoElement.style.display = 'block';
      videoElement.style.visibility = 'visible';
      videoElement.style.opacity = '1';
      videoElement.setAttribute('media-name', mediaAsset.name);
      videoElement.setAttribute('media-id', mediaAsset.id);
      videoElement.setAttribute('content-id', contentId);
      videoElement.setAttribute('playsinline', 'true');
      videoElement.setAttribute('autoplay', 'true');
      videoElement.muted = true; // デフォルトでミュート
      videoElement.classList.add('active');
      applyVideoDownloadSafeguards(videoElement);
      applyMobileMediaLayout(videoElement);
      slideMediaWrapper.appendChild(videoElement);
      activeVideoEl = videoElement;
    }

    // フィードフォルダ商品は常に静止画のため、動画へ切り替わった時点で強制した枠を解除する。
    // 画像分岐でしか解除していないと、連携配信の画像 → 通常の動画で 9:16 の枠と dataset が残ってしまう。
    syncFeedFolderAspectFrame(slideMediaWrapper, content, activeVideoEl);

    applyVideoThumbnailGuard(activeVideoEl, slideMediaWrapper, content, mediaAsset, { force: true });

    // SPのタップ遷移直後は autoplay が効かずに metadata が取れないケースがあるため、
    // 明示的に load/play を試みて「再生/進捗」を安定させる。
    if (activeVideoEl && !useStorySwap) {
      try { activeVideoEl.load(); } catch { void 0; }
      playVideoIfAllowed(activeVideoEl);
    }
    if (volumeIcon) {
      volumeIcon.style.visibility = 'visible';
      if (activeVideoEl && activeVideoEl.muted) {
        volumeIcon.src = volumeOffImg;
      }
    }
  }
  // メディアを appendChild した後でも CTA が前面に残るよう、CTA ホストを末尾へ戻す（iOS の重なり対策）
  ensureCtaHostOnTop(slideMediaWrapper);
  if (isMobileStory()) {
    preloadNextStoryVideo(slideMediaWrapper, contents);
  }
  if (shouldTemporarilyHideWrapper) {
    requestAnimationFrame(() => {
      // mediaの差し替え後に再表示
      slideMediaWrapper.style.visibility = 'visible';
    });
  }
};

// バナーの更新処理
export const updateBanners = () => {
  const banners = queryHeaderElements('.storeeel-movie-select_banner');
  banners.forEach((banner) => {
    banner.style.padding = '1px';
  });
  if (banners[window.moviePosition]) {
    banners[window.moviePosition].style.padding = '2px';
  }
};

// 画像のタイマーの残り時間を取得する関数
export const getImageTimerRemainingTime = () => {
  // タイマーが設定されていない場合は0を返す
  if (imageTimeoutId === null || imageTimerStartTime === null || imageTimerDuration === null) {
    return 0;
  }

  // 経過時間を計算
  const elapsedTime = Date.now() - imageTimerStartTime;

  // 残り時間を計算（ミリ秒単位）
  const remainingTime = Math.max(0, imageTimerDuration - elapsedTime);

  return remainingTime;
};

// 画像のタイマーを一時停止する関数
export const pauseImageTimer = () => {
  if (imageTimeoutId !== null) {
    // 残り時間を取得
    const remainingTime = getImageTimerRemainingTime();

    // タイマーをクリア
    clearTimeout(imageTimeoutId);
    imageTimeoutId = null;

    // 残り時間を保持しておく（再開時に使用するため）
    return remainingTime;
  }
  return 0;
};

// 画像のタイマーを再開する関数
export const resumeImageTimer = (callback, remainingTime) => {
  if (remainingTime > 0) {
    const resumeCallback = callback ?? imageTimerCallback;
    if (!resumeCallback) {
      return null;
    }
    imageTimerStartTime = Date.now();
    imageTimerDuration = remainingTime;
    imageTimerCallback = resumeCallback;
    imageTimeoutId = setTimeout(() => {
      imageTimerCallback?.();
    }, remainingTime);
    return imageTimeoutId;
  }
  return null;
};
